<template>
  <b-layout :colors="$site.colors" :header-title="title" :header-back-route="homeRoute">
    <b-container class="mt-12">
      <div class="sm:flex items-start sm:space-x-4 rtl:space-x-reverse">
        <div class="mb-12 sm:mb-0 w-full sm:w-1/2">
          <b-card
            :title="$t('pages.timetable.selectDate')"
            class="mb-4 "
          >
            <div>
              <div v-if="datesLoading > 0" class="bookapp-overlay-spinner">
                <b-spinner class="bookapp-action-tint-color" />
              </div>
              <VCalendar
                ref="calendar"
                expanded
                title-position="left"
                :attributes="attributes"
                :style="{ opacity: (datesLoading > 0) ? '0.5' : '1' }"
                :locale="$i18n.locale === 'en' ? 'en-GB' : $i18n.locale"
                :columns="cols"
                class="timetable-datepicker"
                @dayclick="dayClicked"
                @did-move="updatePage"
              />
            </div>
          </b-card>
        </div>
        <div class="w-full sm:w-1/2">
          <voucher-alert />

          <div v-if="error" class="error">
            {{ error }}
          </div>

          <div v-else-if="timesLoading" class="loading">
            <b-card
              :title="$t('general.loading')"
              class="mb-4 "
            >
              <div class="text-center">
                <b-spinner label="Loading" class="m-3 bookapp-action-tint-color bookapp-spinner-large" type="grow" />
              </div>
            </b-card>
          </div>
          <div v-else-if="timetable" class="content">
            <b-alert v-if="emptyWeek" show variant="warning">
              {{ $t('general.nothingThisWeek') }}
            </b-alert>

            <template v-for="date in timetable.dates">
              <b-card
                v-if="date.slots.length"
                :key="date.date"
                :title="$format.date(date.date)"
                class="mb-6"
                content-class="bg-primary rounded border overflow-hidden"
              >
                <p v-if="!date.slots.length" class="text-sm text-secondary">
                  {{ $t('general.nothingToday') }}
                </p>

                <div v-else>
                  <a v-for="slot in date.slots" :key="slot.data" class="flex items-center justify-between p-4 hover:bg-action/20 cursor-pointer transition-colors" @click="handleClickSlot(slot, date)">
                    <div class="flex items-center space-x-3  rtl:space-x-reverse">
                      <img v-if="slot.services[0].service.images" :src="slot.services[0].service.images.square" class="h-12 w-12 rounded-full">

                      <div class="text-sm text-secondary">
                        <p>{{ slot.startTimeLabel }}</p>
                        <p :class="slot.available || date.waitlistable ? '' : 'pointer-events-none'" class="font-medium">
                          {{ $t('general.serviceWithEmployee',{service: slot.services[0].service.title, employee: slot.services[0].employee.firstName}) }}
                        </p>
                      </div>
                    </div>

                    <p v-if="$site.settings.bookShowServicePrices && slot.totalPrice > 0" class="text-secondary">
                      {{ slot.totalPriceLabel }}
                    </p>

                    <b-chip v-if="slot.classStatus === 'almost-full'" variant="warning">
                      {{ $t('general.almostFull') }}
                    </b-chip>
                    <b-chip v-if="slot.classStatus === 'full'" variant="danger">
                      {{ $t('general.full') }}
                      <template v-if="date.waitlistable">
                        - {{ $t('general.waitlist') }}
                      </template>
                    </b-chip>
                  </a>
                </div>
              </b-card>
            </template>
          </div>
        </div>
      </div>
    </b-container>
  </b-layout>
</template>

<script>
import { DatePicker as VCalendar } from '@angelblanco/v-calendar';
import '@angelblanco/v-calendar/dist/style.css'
import { useScreens } from 'vue-screen-utils';

import { BookingStage, createBookingRoute } from '@/components/booking/elements/Detail.vue'
import VoucherAlert from '@/components/voucher/elements/Alert.vue'

definePageMeta({
  tabs: ['timetable'],
  middleware: ['redirects']
});

export default defineNuxtComponent({
  components: {
    VCalendar,
    VoucherAlert
  },
  layout: 'default',
  data () {
    return {
      datesLoading: 0,
      timesLoading: false,
      error: null,
      times: null,
      selectedDate: new Date(),
      dateData: [],
      timetable: null
    }
  },
  async asyncData () {
    useTabMeta('timetable')

    const route = useRoute()
    const locationStore = useLocationStore()
    const bookingStore = useBookingStore()

    try { 
      await locationStore.getServices()
      await bookingStore.setFromUrlQuery(route.query)
    } catch (e) {
      error.value = e?.errors
    }

    return {}
  },
  computed: {
    title () {
      return this.$site.tabs.timetable.name
    },
    info () {
      return this.$site.tabs.getInfo('timetable')
    },
    homeRoute () {
      return this.$store.getters.homeRoute || { path: '/' }
    },
    cols () {
      const { mapCurrent } = useScreens({ xs: '0px', sm: '640px', md: '768px', lg: '1024px' })
      return mapCurrent({ lg: 2 }, 1).value
    },
    now () {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      return now
    },
    availableDates () {
      return this.dateData.filter((dateInfo) => {
        return (dateInfo.available && dateInfo.date >= this.now)
      }).map((dateInfo) => {
        return new Date(dateInfo.date.getUTCFullYear(), dateInfo.date.getUTCMonth(), dateInfo.date.getUTCDate(), 0, 0, 0, 0)
      })
    },
    emptyWeek () {
      for (const date of this.timetable.dates) {
        if (date.slots.length > 0) { return false }
      }

      return true
    },
    selectedDateRange () {
      const mondy = this.selectedDate
      const week = new Date(mondy.valueOf() + (86400 * 1000 * 6))

      return {
        start: mondy,
        end: week
      }
    },
    attributes () {
      return [
        {
          highlight: {
            class: 'available',
            style: {
              backgroundColor: this.$site.colors.content['background-2'],
              borderRadius: '5px',
              width: '100%',
              height: '100%'
            },
            contentStyle: {
              color: this.$site.colors.content.text
            }
          },
          dates: this.availableDates
        },
        {
          highlight: {
            style: {
              backgroundColor: this.$site.colors.action.background + '99',
              borderRadius: '5px',
              width: '100%',
              height: '100%'
            },
            contentStyle: {
              color: this.$site.colors.action.text
            }
          },
          dates: [new Date()]
        },
        {
          highlight: {
            style: {
              backgroundColor: this.$site.colors.action.background,
              borderColor: this.$site.colors.action.background,
              borderRadius: '5px',
              width: '130%',
              height: '100%',
              margin: '0 -5px'
            },
            contentStyle: {
              color: this.$site.colors.action.text
            }
          },
          dates: [
            this.selectedDateRange
          ]
        },
      ]
    },
    voucher () {
      return this.$store.getters['booking/voucher']
    }
  },
  watch: {
    voucher () {
      this.fetchAvailableDates(this.selectedDate)
      this.fetchTimetable(this.selectedDate)
    }
  },
  created () {
    // this.fetchAvailableDates(this.selectedDate)
    // if (this.initialTimetable) this.timetable = this.initialTimetable

    if (this.$store.getters['booking/selectedDate']) {
      this.selectedDate = this.$store.getters['booking/selectedDate']
    } else {
      this.selectedDate = new Date()
    }
  },
  mounted () {
    // this.fetchAvailableDates(this.selectedDate)
    if (!this.timetable) { this.fetchTimetable(this.selectedDate) }

    setTimeout(() => {
      if (this.$refs.calendar) {
        this.$refs.calendar.move(this.selectedDate)
      }
    }, 0)
  },
  methods: {
    slotTooltip (slot) {
      let tooltip = ''
      for (let i = 0; i < slot.services.length; i++) {
        const slotService = slot.services[i]

        tooltip += slotService.service.title + ' with ' + slotService.employee.firstName
      }

      if (slot.totalPrice > 0) {
        tooltip += ': ' + slot.totalPriceLabel
      }

      return tooltip
    },
    async fetchAvailableDates (date) {
      this.datesLoading++

      const response = await this.$api.booking.timetableMonth(date, (this.voucher && this.voucher.isValid) ? this.voucher : null)
      this.datesLoading--

      for (let i = 0; i < response.dates.length; i++) {
        const date = response.dates[i]

        const contains = this.dateData.some((dateInfo) => {
          return dateInfo.date.getUTCFullYear() === date.date.getUTCFullYear() && dateInfo.date.getUTCMonth() === date.date.getUTCMonth() && dateInfo.date.getUTCDate() === date.date.getUTCDate()
        })

        if (!contains) {
          this.dateData.push(date)
        }
      }
    },
    async fetchTimetable (date) {
      this.timesLoading = true
      this.availableTimes = null

      try {
        const response = await this.$api.booking.timetableWeek(date, (this.voucher && this.voucher.isValid) ? this.voucher : null)
        this.timesLoading = false

        this.timetable = response
      } catch (e) {
        this.timesLoading = false
        this.error = e?.errors
      }
    },
    dayClicked (day, event) {
      event.target.blur()
      
      this.$store.dispatch('booking/setSelectedDate', day.date)

      if (day.date >= this.now) {
        this.selectedDate = day.date
      } else {
        this.selectedDate = new Date()
      }
      this.fetchTimetable(this.selectedDate)
    },
    updatePage (page) {
      const d = new Date(page.year, page.month - 1, 1, 0, 0, 0, 0)
      this.fetchAvailableDates(d)
    },
    setSelectionsForSlot (slot) {
      this.$store.dispatch('booking/clearServiceSelections')

      for (let i = 0; i < slot.services.length; i++) {
        const slotService = slot.services[i]

        this.$store.dispatch('booking/setServiceSelected', { service: slotService.service, selected: true, toBack: true })
      }

      this.$store.dispatch('booking/setSelectedSlot', { slot, mode: 'timetable' })
    },
    handleClickSlot (slot, date) {
      if (slot.available) {
        this.bookSlot(slot)
      } else if (date.waitlistable) {
        this.waitlistSlot(slot, date)
      }
    },
    waitlistSlot (slot, date) {
      this.setSelectionsForSlot(slot)
      this.$store.dispatch('booking/setSelectedDate', new Date(date.date * 1000))

      const bookingUrlQuery = this.$store.getters['booking/urlQuery']
      this.$router.push(createBookingRoute(bookingUrlQuery, BookingStage.WAITLIST_CUSTOMER))
    },
    bookSlot (slot) {
      this.setSelectionsForSlot(slot)
      const bookingUrlQuery = this.$store.getters['booking/urlQuery']

      if (this.$site.settings.showGroupDetail) {
        this.$router.push(createBookingRoute(bookingUrlQuery, BookingStage.DETAIL))
      } else {
        this.$router.push(createBookingRoute(bookingUrlQuery, BookingStage.CUSTOMER))
      }
    }
  }
})
</script>

<style>
.timetable-datepicker .vc-highlights {
  overflow: visible !important;
}

.timetable-datepicker .vc-highlight.vc-highlight-base-start {
  margin: 0 -5px 0 0 !important;
}

.timetable-datepicker .vc-highlight.vc-highlight-base-end {
  margin: 0 0 0 -5px !important;
}
</style>
